import React from "react";
import get from "lodash/get";
import {
  ResponsiveHeroImage,
  LoadMoreButton,
  LazyLoadImages,
  ReviewRating,
  wrapCollectionLayout
} from "@quintype/components";
import { CustomLink } from "../custom-link";
import "./four-col-grid.module.css";
import { ImageFallback } from "../basic/image-fallback.js";
import {
  altTagOfHeroImages,
  isExternalStory,
  isEager,
  getRowMetadata,
  updateStoriesWithAlternatives
} from "../../utils";
import { CollectionName } from "../atoms/collection-name/index.js";
import PropTypes from "prop-types";

const themeStyles = {
  dark: "theme-dark"
};

function FourColGridBase({ collection, stories, associatedMetadata, onLoadMore, noMoreStories = true, index, config }) {
  const pbAheadConfig = getRowMetadata(config, associatedMetadata);
  const {
    theme = "dark",
    hide_section: hideSection = false,
    show_collection_title: collectionName = true,
    limit_stories: limitStories = 0
  } = pbAheadConfig;

  const alwaysEager = isEager(index, true);
  const desktopEager = isEager(index);

  if (limitStories) stories = stories.slice(0, limitStories);
  stories = updateStoriesWithAlternatives(stories, false);
  return (
    <div className="component-wrapper" data-test-id="four-col-grid">
      {collectionName ? <CollectionName name={collection.name} config={config} section={collection} /> : null}
      <div styleName="stories" data-test-id="stories">
        {stories.map((story, index) => {
          const isReviewStory = story["story-template"] === "review";
          return (
            <div key={`${index}-${story.id}`}>
              <CustomLink href={`${story.url}`} externalLink={isExternalStory(story)}>
                {story["hero-image-s3-key"] ? (
                  <figure styleName={`image ${story["story-template"] === "video" ? "video" : ""}`}>
                    <LazyLoadImages margin={"0px"}>
                      <ResponsiveHeroImage
                        story={story}
                        aspectRatio={[16, 9]}
                        alt={altTagOfHeroImages(story)}
                        defaultWidth={480}
                        widths={[300, 420]}
                        sizes="(max-width: 420px) 80vw, (max-width: 768px) 48vw, 5vw"
                        imgParams={{ auto: ["format", "compress"], fit: "max" }}
                        eager={index < 2 ? alwaysEager : desktopEager}
                      />
                    </LazyLoadImages>
                  </figure>
                ) : (
                  <ImageFallback styleName="image-cover" story={story} />
                )}
                <div styleName="relative">
                  {!hideSection && (
                    <div styleName="section-name" data-test-id="four-col-grid-section-name">
                      {story.sections[0]["display-name"] || story.sections[0]["name"]}
                    </div>
                  )}
                  <h2 styleName={`headline ${themeStyles[theme] || ""}`} data-test-id="four-col-grid-headline">
                    <bdi dangerouslySetInnerHTML={{ __html: story.headline }} />
                    {isReviewStory && <ReviewRating value={get(story, ["metadata", "review-rating", "value"], "0")} />}
                  </h2>
                </div>
              </CustomLink>
            </div>
          );
        })}
      </div>
      {!noMoreStories && <LoadMoreButton className="load-more-button" onLoadMore={onLoadMore} text="Load More" />}
    </div>
  );
}

FourColGridBase.propTypes = {
  config: PropTypes.object,
  collection: PropTypes.object,
  stories: PropTypes.array,
  associatedMetadata: PropTypes.object,
  onLoadMore: PropTypes.func,
  noMoreStories: PropTypes.bool,
  index: PropTypes.number
};

export const FourColGrid = wrapCollectionLayout(FourColGridBase);
